import React from 'react'
import RouterLink from 'redux-first-router-link'
import cx from 'classnames'
import { t } from 'ttag'
import { useInternalState } from './hooks'
import { Icon } from 'components/shared/Icon/Icon'
import { ICON_SIZES, SIZE_SMALL } from './model'
import styles from './Link.module.scss'
import spinner from 'assets/icons/spinner.svg'

type LinkBaseProps = {
  className?: string
  color?: string
  icon?: React.ComponentProps<typeof Icon>['icon']
  label?: React.ReactNode
  size?: string
  target?: string
  variant?: string
  isContentReversed?: boolean
}

/** Navigates inside the app: `to` is a redux-first-router destination. */
export type RouterLinkProps = LinkBaseProps & {
  isExternal?: false
  // Required: `RouterLink` has no meaningful behaviour without a destination,
  // and every call site already passes one.
  to: React.ComponentProps<typeof RouterLink>['to']
  redirect?: boolean
  filename?: never
}

/**
 * Downloads a file: `to` is a URL handed straight to `fetch`, and the element
 * rendered is a `<button>`, not a link.
 *
 * `to` carries a different type in each variant, which is why these are split.
 * Before `redux-first-router-link` had types, `to` was `any` and a router
 * destination object could be passed here and silently reach `fetch`.
 */
type ExternalLinkProps = LinkBaseProps & {
  isExternal: true
  to?: string
  filename?: string
  redirect?: never
}

type LinkProps = RouterLinkProps | ExternalLinkProps

export const Link = (props: LinkProps) => {
  const {
    className,
    color,
    icon,
    label,
    size,
    target,
    variant,
    isContentReversed = false,
  } = props

  const {
    state: { downloading },
    handlers: { handleDownload },
  } = useInternalState({
    to: props.isExternal ? props.to : undefined,
    filename: props.isExternal ? props.filename : undefined,
  })

  const classNames = cx(
    styles.link,
    {
      [styles.size]: Boolean(size),
      [styles.variant]: Boolean(variant),
      [styles.color]: Boolean(color),
      [styles.reversed]: isContentReversed,
    },
    size && styles[size],
    variant && styles[variant],
    color && styles[color],
    className,
  )

  const content = (
    <>
      {icon ? (
        <Icon
          className={cx(styles.icon, {
            [styles.nospacing]: !Boolean(label),
            [styles.hide]: downloading,
          })}
          icon={icon}
          size={size && ICON_SIZES[size]}
        />
      ) : null}
      {label ? (
        <span
          className={cx(styles.label, {
            [styles.nospacing]: !Boolean(icon),
            [styles.hide]: downloading,
          })}
        >
          {label}
        </span>
      ) : null}
      {downloading ? (
        <div className={cx(styles.spinnerContainer)}>
          <img
            src={spinner}
            alt={t`Downloading`}
            className={cx(styles.spinner, {
              [styles.small]: size === SIZE_SMALL,
            })}
          />
        </div>
      ) : null}
    </>
  )

  // Rendered as two branches rather than one dynamic `Component`, because the
  // props differ per branch and a `'button' | typeof RouterLink` union cannot
  // be type-checked against a single spread.
  return props.isExternal ? (
    <button
      className={classNames}
      onClick={downloading ? () => {} : handleDownload}
      {...{ target }}
    >
      {content}
    </button>
  ) : (
    <RouterLink
      className={classNames}
      to={props.to}
      redirect={props.redirect ?? false}
      {...{ target }}
    >
      {content}
    </RouterLink>
  )
}
